import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import VerifiedBadge from '@/components/VerifiedBadge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trophy, Medal, Award } from 'lucide-react';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function Leaderboard({ user, onLogout }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [selectedTime, setSelectedTime] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [userRankData, setUserRankData] = useState(null); // NEW: Store current user's rank data
  const entriesPerPage = 50;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [selectedCourse, selectedTime, selectedLocation]);

  const fetchData = async () => {
    try {
      const [leaderboardRes, coursesRes] = await Promise.all([
        axios.get(`${API}/leaderboard/overall`, { headers: getAuthHeaders() }),
        axios.get(`${API}/courses`, { headers: getAuthHeaders() })
      ]);

      setLeaderboard(leaderboardRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    try {
      let url = `${API}/leaderboard/overall`;
      
      if (selectedCourse !== 'all') {
        url = `${API}/leaderboard/course/${selectedCourse}`;
      }
      
      // Add time period parameter
      const params = new URLSearchParams();
      if (selectedTime !== 'all') {
        params.append('time_period', selectedTime);
      }
      
      const fullUrl = params.toString() ? `${url}?${params.toString()}` : url;
      
      const res = await axios.get(fullUrl, { headers: getAuthHeaders() });
      let filteredData = res.data;

      // Apply location filter on frontend (after receiving backend data)
      if (selectedLocation === 'city') {
        filteredData = filteredData.filter(entry => entry.city === user.city);
      } else if (selectedLocation === 'school') {
        filteredData = filteredData.filter(entry => entry.school_name === user.school_name);
      }

      setLeaderboard(filteredData);
      setCurrentPage(1);
      
      // FIXED: Find current user's rank in the filtered leaderboard
      const currentUserRank = filteredData.find(entry => entry.user_id === user.id);
      setUserRankData(currentUserRank);
    } catch (error) {
      console.error('Failed to apply filters:', error);
    }
  };

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('http')) return photoPath;
    return `${BACKEND_URL}${photoPath}`;
  };

  const getInitials = (name) => {
    const parts = name.split(' ');
    return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="leaderboard"><div className="p-8">Loading...</div></Layout>;
  }

  const totalPages = Math.ceil(leaderboard.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const paginatedLeaderboard = leaderboard.slice(startIndex, startIndex + entriesPerPage);

  return (
    <Layout user={user} onLogout={onLogout} currentPage="leaderboard">
      <div className="p-6 lg:p-8 space-y-6" data-testid="student-leaderboard">
        <div>
          <h1 className="text-3xl font-bold text-navy">Leaderboard</h1>
          <p className="text-[#4D4D4D] mt-2 font-light">See how you rank among your peers</p>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold mb-2 text-navy">Course</label>
            <Select value={selectedCourse} onValueChange={setSelectedCourse}>
              <SelectTrigger className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth">
                <SelectValue placeholder="Select course" />
              </SelectTrigger>
              <SelectContent className="bg-[#F0F8FF] border-2 border-[#90CAF9]">
                <SelectItem value="all">All Courses</SelectItem>
                {courses.map((course) => (
                  <SelectItem key={course.id} value={course.id}>
                    {course.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-navy">Time Period</label>
            <Select value={selectedTime} onValueChange={setSelectedTime}>
              <SelectTrigger className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth">
                <SelectValue placeholder="Select time period" />
              </SelectTrigger>
              <SelectContent className="bg-[#F0F8FF] border-2 border-[#90CAF9]">
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-navy">Location</label>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent className="bg-[#F0F8FF] border-2 border-[#90CAF9]">
                <SelectItem value="all">All India</SelectItem>
                <SelectItem value="city">My City ({user.city || 'Not set'})</SelectItem>
                <SelectItem value="school">My School ({user.school_name || 'Not set'})</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Your Rank Card - IMPROVED: Clean, aligned, solid background */}
        {userRankData && (
          <Card className="border-0 card-shadow bg-[#E3F2FD] rounded-2xl overflow-hidden">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-navy mb-5 flex items-center gap-2">
                <Trophy className="w-6 h-6 text-primary-blue" strokeWidth={2.5} />
                Your Rank
              </h3>
              <div className="flex items-center justify-between p-6 bg-white rounded-2xl shadow-sm">
                <div className="flex items-center gap-5">
                  {/* Rank Badge - IMPROVED: Solid blue background, clear contrast */}
                  <div className="flex-shrink-0 w-20 h-20 rounded-full bg-gradient-to-br from-[#1E88E5] to-[#1976D2] flex items-center justify-center shadow-lg border-4 border-white">
                    <div className="text-center">
                      <p className="text-xs font-semibold text-white/90 uppercase tracking-wide">Rank</p>
                      <p className="text-3xl font-extrabold text-white leading-none mt-0.5">#{userRankData.rank}</p>
                    </div>
                  </div>
                  
                  {/* User Info - IMPROVED: Better alignment and spacing */}
                  <div className="flex items-center gap-4">
                    <Avatar className="w-14 h-14 border-3 border-primary-blue shadow-md">
                      <AvatarImage src={user.profile_photo ? `${BACKEND_URL}${user.profile_photo}` : ''} />
                      <AvatarFallback className="bg-gradient-to-br from-primary-blue to-blue-400 text-white text-xl font-bold">
                        {user.first_name?.[0]}{user.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-lg font-bold text-navy truncate">{userRankData.name}</p>
                        {user.profile_verified && <VerifiedBadge />}
                      </div>
                      <p className="text-sm font-medium text-[#666] truncate">{user.school_name || 'No school'}</p>
                    </div>
                  </div>
                </div>
                
                {/* Stats - IMPROVED: Clearer layout, better contrast */}
                <div className="flex items-center gap-8">
                  <div className="text-center px-4 py-2 rounded-xl bg-blue-50 border border-blue-100">
                    <p className="text-xs font-semibold text-[#666] uppercase tracking-wide mb-1">XP Points</p>
                    <p className="text-3xl font-extrabold text-primary-blue">{userRankData.xp_points || 0}</p>
                  </div>
                  <div className="text-center px-4 py-2 rounded-xl bg-orange-50 border border-orange-100">
                    <p className="text-xs font-semibold text-[#666] uppercase tracking-wide mb-1">Streak</p>
                    <p className="text-3xl font-extrabold text-orange-500">{userRankData.daily_streak || 0}🔥</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Leaderboard Card */}
        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardContent className="p-6">
            {leaderboard.length === 0 ? (
              <div className="text-center py-16">
                <Trophy className="w-20 h-20 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
                <p className="text-lg font-semibold text-navy">No leaderboard data available</p>
                <p className="text-sm text-[#4D4D4D] mt-2">Try changing your filters</p>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {paginatedLeaderboard.map((entry) => (
                  <div
                    key={entry.user_id}
                    className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-5 rounded-2xl transition-smooth hover:shadow-md gap-3 sm:gap-0 ${
                      entry.user_id === user.id ? 'bg-gradient-to-r from-[#E3F2FD] to-[#BBDEFB] ring-2 ring-primary-blue' : 'bg-[#F7F7F7] hover:bg-[#E7E7E7]'
                    }`}
                    data-testid={`leaderboard-entry-${entry.rank}`}
                  >
                    <div className="flex items-center space-x-3 sm:space-x-4 w-full sm:w-auto">
                      <div className="relative flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
                        {entry.rank <= 3 ? (
                          <div className="relative w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center flex-shrink-0">
                            {/* Medal-Specific Background Circle */}
                            <div className={`absolute inset-0 rounded-full opacity-50 ${
                              entry.rank === 1 ? 'bg-gradient-to-br from-[#FFF8DC] to-[#FFE4B5]' :
                              entry.rank === 2 ? 'bg-gradient-to-br from-[#F5F5F5] to-[#E0E0E0]' :
                              'bg-gradient-to-br from-[#FFE4C4] to-[#F4A460]'
                            }`}></div>
                            
                            {/* 3D Medal with Ribbon */}
                            <svg className="w-8 h-8 sm:w-11 sm:h-11 relative z-10" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <defs>
                                {/* Gradient for Gold */}
                                <radialGradient id={`gold-${entry.rank}`} cx="50%" cy="40%">
                                  <stop offset="0%" stopColor="#FFD700" />
                                  <stop offset="50%" stopColor="#FFA500" />
                                  <stop offset="100%" stopColor="#FF8C00" />
                                </radialGradient>
                                {/* Gradient for Silver */}
                                <radialGradient id={`silver-${entry.rank}`} cx="50%" cy="40%">
                                  <stop offset="0%" stopColor="#F0F0F0" />
                                  <stop offset="50%" stopColor="#C0C0C0" />
                                  <stop offset="100%" stopColor="#A0A0A0" />
                                </radialGradient>
                                {/* Gradient for Bronze */}
                                <radialGradient id={`bronze-${entry.rank}`} cx="50%" cy="40%">
                                  <stop offset="0%" stopColor="#E89C5C" />
                                  <stop offset="50%" stopColor="#CD7F32" />
                                  <stop offset="100%" stopColor="#8B4513" />
                                </radialGradient>
                              </defs>
                              
                              {/* Ribbon Left */}
                              <path 
                                d="M 18 8 L 16 2 L 20 2 L 22 10 L 18 8 Z" 
                                fill={entry.rank === 1 ? '#1E90FF' : entry.rank === 2 ? '#DC143C' : '#FF6347'}
                                opacity="0.9"
                              />
                              {/* Ribbon Right */}
                              <path 
                                d="M 30 8 L 32 2 L 28 2 L 26 10 L 30 8 Z" 
                                fill={entry.rank === 1 ? '#1E90FF' : entry.rank === 2 ? '#DC143C' : '#FF6347'}
                                opacity="0.9"
                              />
                              
                              {/* Medal Outer Circle - 3D Shadow */}
                              <circle 
                                cx="24" 
                                cy="28" 
                                r="14" 
                                fill="url(#${entry.rank === 1 ? 'gold' : entry.rank === 2 ? 'silver' : 'bronze'}-${entry.rank})"
                                filter="drop-shadow(0 4px 6px rgba(0,0,0,0.3))"
                              />
                              
                              {/* Medal Inner Ring */}
                              <circle 
                                cx="24" 
                                cy="28" 
                                r="12.5" 
                                fill="none" 
                                stroke={entry.rank === 1 ? '#B8860B' : entry.rank === 2 ? '#808080' : '#8B4513'}
                                strokeWidth="1"
                                opacity="0.6"
                              />
                              
                              {/* Medal Center Circle */}
                              <circle 
                                cx="24" 
                                cy="28" 
                                r="10" 
                                fill={entry.rank === 1 ? '#FFD700' : entry.rank === 2 ? '#E8E8E8' : '#E89C5C'}
                                opacity="0.95"
                              />
                              
                              {/* Inner Detail Ring */}
                              <circle 
                                cx="24" 
                                cy="28" 
                                r="8.5" 
                                fill="none" 
                                stroke={entry.rank === 1 ? '#B8860B' : entry.rank === 2 ? '#A0A0A0' : '#8B4513'}
                                strokeWidth="0.5"
                                opacity="0.4"
                              />
                              
                              {/* Rank Number */}
                              <text 
                                x="24" 
                                y="33" 
                                textAnchor="middle" 
                                fontSize="14" 
                                fontWeight="900"
                                fill={entry.rank === 1 ? '#B8860B' : entry.rank === 2 ? '#707070' : '#8B4513'}
                              >
                                {entry.rank}
                              </text>
                              
                              {/* Highlight/Shine Effect */}
                              <ellipse 
                                cx="20" 
                                cy="24" 
                                rx="4" 
                                ry="3" 
                                fill="white" 
                                opacity="0.4"
                              />
                            </svg>
                          </div>
                        ) : (
                          <div
                            className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-white text-sm sm:text-lg shadow-lg flex-shrink-0 ${
                              entry.rank <= 10 ? 'bg-primary-blue' : 'bg-[#A5A5A5]'
                            }`}
                          >
                            {entry.rank}
                          </div>
                        )}
                        <Avatar className="w-10 h-10 sm:w-12 sm:h-12 border-2 border-white shadow-md flex-shrink-0">
                          <AvatarImage src={getPhotoUrl(entry.profile_photo)} />
                          <AvatarFallback className="text-sm font-semibold bg-primary-blue text-white">
                            {getInitials(entry.name)}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm sm:text-[17px] text-navy flex flex-wrap items-center gap-1 sm:gap-2">
                          <span className="truncate">{entry.name}</span>
                          <VerifiedBadge show={entry.profile_verified} size="sm" />
                          {entry.user_id === user.id && <span className="text-xs px-2 sm:px-3 py-1 rounded-full bg-primary-blue text-white font-semibold whitespace-nowrap">You</span>}
                        </p>
                        <p className="text-xs sm:text-sm text-[#4D4D4D] truncate">{entry.city}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto gap-4 sm:gap-0 border-t sm:border-t-0 pt-3 sm:pt-0 sm:text-right">
                      <div className="flex flex-col sm:flex-col-reverse gap-1">
                        <p className="text-xs sm:text-sm font-semibold text-[#4D4D4D] sm:order-2">{entry.daily_streak} day streak</p>
                        <div className="flex items-center gap-1 sm:gap-2 sm:justify-end sm:order-1">
                          <p className="font-black text-xl sm:text-2xl text-navy" data-testid={`xp-points-${entry.rank}`}>{entry.xp_points}</p>
                          <span className="text-xs sm:text-sm font-semibold text-primary-blue">GP</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center space-x-2 mt-6 pt-6 border-t border-[#E7E7E7]">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 rounded-xl bg-[#F7F7F7] hover:bg-primary-blue hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-smooth font-medium"
                    >
                      Previous
                    </button>
                    
                    <div className="flex space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-4 py-2 rounded-xl font-medium transition-smooth ${
                              currentPage === pageNum
                                ? 'bg-primary-blue text-white'
                                : 'bg-[#F7F7F7] text-navy hover:bg-[#E7E7E7]'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 rounded-xl bg-[#F7F7F7] hover:bg-primary-blue hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-smooth font-medium"
                    >
                      Next
                    </button>
                    
                    <span className="text-sm text-[#4D4D4D] ml-4">
                      Page {currentPage} of {totalPages} ({leaderboard.length} entries)
                    </span>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}